import { useEffect, useRef } from 'react';

import { useRouter } from 'react-resource-router';

import { getDocument } from '@atlaskit/browser-apis';

import {
	BENCHMARK_ROUTE_NAME,
	CLI_ROUTE_NAME,
	HOME_ROUTE_NAME,
	type MicrositeRouteName,
} from '@atlassian/twg-microsite-experience/routes';

/**
 * The browser `<title>` for each route.
 *
 * Home and CLI duplicate their dedicated document titles. The Benchmark title
 * is application-only while that gated route shares the home document.
 * `src/__tests__/html-metadata.test.ts` guards the document-backed entries.
 */
export const ROUTE_TITLES: Record<MicrositeRouteName, string> = {
	[HOME_ROUTE_NAME]: 'Atlassian Teamwork Graph',
	[CLI_ROUTE_NAME]: 'Atlassian Teamwork Graph CLI',
	[BENCHMARK_ROUTE_NAME]: 'Atlassian Teamwork Graph Benchmark',
};

const isMicrositeRouteName = (name: string | undefined): name is MicrositeRouteName =>
	name === HOME_ROUTE_NAME || name === CLI_ROUTE_NAME || name === BENCHMARK_ROUTE_NAME;

/**
 * Keeps the browser tab title in step with the route.
 *
 * Home and CLI serve route-specific HTML documents, but Benchmark uses the home
 * document while it remains feature-gated. SPA navigation also swaps the route
 * without fetching a destination document. This hook keeps the tab title aligned
 * in both cases.
 *
 * Only the title is synchronised. `canonical` and the `og:*`/`twitter:*` tags are
 * deliberately left alone: search engines and social unfurlers fetch each URL
 * directly and never see the result of an in-app navigation, so rewriting them
 * client-side would add moving parts without changing what any crawler reads.
 *
 * The first render is a no-op for routes with dedicated documents. Benchmark is
 * updated immediately because it intentionally has no dedicated HTML entry.
 */
export const useDocumentTitleOnRouteChange = (): void => {
	const [{ route }] = useRouter();
	const isFirstRender = useRef(true);

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			if (route?.name !== BENCHMARK_ROUTE_NAME) {
				return;
			}
		}

		// An unknown route resolves to the catch-all, which redirects to `/`. Leaving
		// the title alone means the tab keeps the previous route's title for the one
		// render that pass takes, rather than flashing something wrong.
		const routeName = route?.name;
		if (!isMicrositeRouteName(routeName)) {
			return;
		}

		const doc = getDocument();
		if (doc) {
			doc.title = ROUTE_TITLES[routeName];
		}
	}, [route?.name]);
};
